// src/config/photoTasks.js
// Enterprise Definitions for Step-by-Step Biometric Capture (Selfies 1-5, Pose A 1-4, Pose B 5-8)

export const SELFIE_TASKS = [
  {
    id: 'selfie_1',
    number: 1,
    stage: 'Present_Neutral',
    title: 'Selfie 1 of 5',
    headline: 'Frontal Neutral — No Glasses',
    caption: 'Neutral facial expression, eyewear removed, direct camera eye-contact.',
    noGlasses: true,
    rules: [
      { text: 'Eyewear prohibited: remove all glasses or tinted lenses for full facial recognition', type: 'prohibit' },
      { text: 'Neutral expression: mouth closed with relaxed facial muscles (no smiling)', type: 'expression' },
      { text: 'Direct eye contact: maintain gaze level with camera lens', type: 'gaze' },
      { text: 'Balanced illumination: ensure even lighting across both sides of the face', type: 'light' },
      { text: 'Authentic camera capture: beauty filters, smoothing, and digital edits prohibited', type: 'authentic' }
    ],
    clause: 'Biometric Liveness Requirement: Capture in a well-lit space. After capture, change background location for the subsequent selfie.',
    fallbackDirection: 'STRAIGHT_AHEAD'
  },
  {
    id: 'selfie_2',
    number: 2,
    stage: 'Present_Neutral',
    title: 'Selfie 2 of 5',
    headline: 'Frontal Natural — Distinct Background',
    caption: 'Natural relaxed posture in a new physical environment. Daily prescription eyewear permitted.',
    noGlasses: false,
    rules: [
      { text: 'Mandatory background change: position yourself against a different wall or room', type: 'background' },
      { text: 'Standard eyewear permitted if worn daily (no reflective glare or sunglasses)', type: 'eyewear' },
      { text: 'Natural relaxed facial posture facing directly forward', type: 'expression' },
      { text: 'Center face alignment: ensure forehead and chin are within the oval frame', type: 'framing' }
    ],
    clause: 'Spatial Diversity Standard: Changing physical background location guarantees valid multi-lighting model training.',
    fallbackDirection: 'STRAIGHT_AHEAD'
  },
  {
    id: 'selfie_3',
    number: 3,
    stage: 'Present_Neutral',
    title: 'Selfie 3 of 5',
    headline: 'Frontal Capture — Ambient Light Shift',
    caption: 'Reposition relative to available room lighting (near a window or alternate light source).',
    noGlasses: false,
    rules: [
      { text: 'Shift lighting angle: capture under alternate natural or ambient light', type: 'light' },
      { text: 'Sharp optical focus: hold device steady to prevent motion blur', type: 'quality' },
      { text: 'Unobstructed facial contours: hair, hands, and headwear must not obscure face', type: 'prohibit' },
      { text: 'Frontal alignment maintained at eye level', type: 'gaze' }
    ],
    clause: 'Photometric Consistency Standard: Multi-source ambient lighting enables robust training across varied illumination conditions.',
    fallbackDirection: 'STRAIGHT_AHEAD'
  },
  {
    id: 'selfie_4',
    number: 4,
    stage: 'Present_Neutral',
    title: 'Selfie 4 of 5',
    headline: 'Frontal Capture — Eyewear Removed',
    caption: 'Capture without eyewear to reinforce unobstructed orbital feature verification.',
    noGlasses: true,
    rules: [
      { text: 'Eyewear removed: eyes, eyebrows, and bridge of nose must be fully visible', type: 'prohibit' },
      { text: 'Distinct background: move to a 4th separate background area', type: 'background' },
      { text: 'Square shoulders with direct forward head orientation', type: 'posture' },
      { text: 'Steady device hold: maintain minimum 45 cm distance from camera', type: 'framing' }
    ],
    clause: 'Feature Verification Standard: High-resolution unobstructed orbital captures ensure precise landmark positioning.',
    fallbackDirection: 'STRAIGHT_AHEAD'
  },
  {
    id: 'selfie_5',
    number: 5,
    stage: 'Present_Neutral',
    title: 'Selfie 5 of 5',
    headline: 'Frontal Capture — Final Validation Shot',
    caption: 'Final front-facing verification capture. Subtle natural smile permitted.',
    noGlasses: false,
    rules: [
      { text: 'Final distinct background location for comprehensive sequence diversity', type: 'background' },
      { text: 'Natural pleasant posture: subtle natural expression accepted', type: 'expression' },
      { text: 'Face fully encompassed inside the green assurance oval', type: 'framing' },
      { text: 'Ensure crisp focus and clear facial landmark visibility', type: 'quality' }
    ],
    clause: 'Sequence Completion: Upon capture, your live selfie set satisfies multi-background diversity criteria.',
    fallbackDirection: 'STRAIGHT_AHEAD'
  }
];

export const POSE_A_TASKS = [
  {
    id: 'pose_1',
    number: 1,
    arrow: '⬆️',
    stage: 'Pose_Variation_A',
    title: 'Pose 1 of 4: Upward Pitch',
    headline: 'Look UP (+15° to +25°)',
    caption: 'Tilt chin upward toward the ceiling while maintaining horizontal symmetry.',
    targetPitch: '+15° to +25° (Upward)',
    targetYaw: '0° ±15° (Centered)',
    rules: [
      { text: 'Elevate chin upward by approximately 20 degrees', type: 'pitch' },
      { text: 'Maintain horizontal symmetry without turning head sideways', type: 'symmetry' },
      { text: 'Align gaze upward with the head movement', type: 'gaze' },
      { text: 'Hold position steadily once guidance ring locks green', type: 'hold' }
    ],
    clause: 'Geometric Yaw-Pitch Calibration: Elevate chin until the directional vector turns green to trigger capture.'
  },
  {
    id: 'pose_2',
    number: 2,
    arrow: '⬇️',
    stage: 'Pose_Variation_A',
    title: 'Pose 2 of 4: Downward Pitch',
    headline: 'Look DOWN (-15° to -25°)',
    caption: 'Tilt chin downward toward your chest while keeping head horizontally centered.',
    targetPitch: '-15° to -25° (Downward)',
    targetYaw: '0° ±15° (Centered)',
    rules: [
      { text: 'Depress chin downward toward floor by approximately 20 degrees', type: 'pitch' },
      { text: 'Keep head centered without tilting sideways', type: 'symmetry' },
      { text: 'Avoid tucking chin excessively against neck or chest', type: 'posture' },
      { text: 'Maintain steady hold for automated 600ms green ring validation', type: 'hold' }
    ],
    clause: 'Depression Metric Calibration: Smoothly lower chin until the downward indicator validates.'
  },
  {
    id: 'pose_3',
    number: 3,
    arrow: '⬅️',
    stage: 'Pose_Variation_A',
    title: 'Pose 3 of 4: Left Azimuth Turn',
    headline: 'Turn LEFT (+20° to +35°)',
    caption: 'Rotate head to your left side. Your right ear and jawline contour must remain visible.',
    targetPitch: '0° ±15° (Level)',
    targetYaw: '+20° to +35° (Left)',
    rules: [
      { text: 'Rotate head to YOUR LEFT by approximately 25 degrees', type: 'yaw' },
      { text: 'Keep chin level with horizon (avoid upward or downward tilt)', type: 'level' },
      { text: 'Three-quarter anatomical facial profile angle', type: 'posture' },
      { text: 'Hold position steadily for automated biometric confirmation', type: 'hold' }
    ],
    clause: 'Azimuth Calibration: Rotate head to the left until the indicator locks into the green range.'
  },
  {
    id: 'pose_4',
    number: 4,
    arrow: '➡️',
    stage: 'Pose_Variation_A',
    title: 'Pose 4 of 4: Right Azimuth Turn',
    headline: 'Turn RIGHT (-20° to -35°)',
    caption: 'Rotate head to your right side. Your left ear and jawline contour must remain visible.',
    targetPitch: '0° ±15° (Level)',
    targetYaw: '-20° to -35° (Right)',
    rules: [
      { text: 'Rotate head to YOUR RIGHT by approximately 25 degrees', type: 'yaw' },
      { text: 'Keep chin level with horizon (avoid upward or downward tilt)', type: 'level' },
      { text: 'Three-quarter anatomical facial profile angle', type: 'posture' },
      { text: 'Hold position steadily for automated biometric confirmation', type: 'hold' }
    ],
    clause: 'Azimuth Calibration: Rotate head to the right until the indicator locks into the green range.'
  }
];

export const POSE_B_TASKS = [
  {
    id: 'pose_5',
    number: 5,
    arrow: '↖️',
    stage: 'Pose_Variation_B',
    title: 'Pose 5 of 8: Compound Diagonal UP-LEFT',
    headline: 'Diagonal UP + LEFT',
    caption: 'Compound angle: Tilt chin upward (+15° to +25°) while rotating head left (+20° to +35°).',
    targetPitch: '+15° to +25° (Upward)',
    targetYaw: '+20° to +35° (Left)',
    rules: [
      { text: 'Simultaneously elevate chin upward (15° to 25°)', type: 'pitch' },
      { text: 'Rotate head toward your left (20° to 35°)', type: 'yaw' },
      { text: 'Maintain upper-left diagonal compound alignment', type: 'angle' },
      { text: 'Hold steady inside green target quadrant for automated capture', type: 'hold' }
    ],
    clause: 'Upper-Left Diagonal Metric: Align head orientation into upper-left quadrant until ring turns green.'
  },
  {
    id: 'pose_6',
    number: 6,
    arrow: '↗️',
    stage: 'Pose_Variation_B',
    title: 'Pose 6 of 8: Compound Diagonal UP-RIGHT',
    headline: 'Diagonal UP + RIGHT',
    caption: 'Compound angle: Tilt chin upward (+15° to +25°) while rotating head right (-20° to -35°).',
    targetPitch: '+15° to +25° (Upward)',
    targetYaw: '-20° to -35° (Right)',
    rules: [
      { text: 'Simultaneously elevate chin upward (15° to 25°)', type: 'pitch' },
      { text: 'Rotate head toward your right (-20° to -35°)', type: 'yaw' },
      { text: 'Maintain upper-right diagonal compound alignment', type: 'angle' },
      { text: 'Hold steady inside green target quadrant for automated capture', type: 'hold' }
    ],
    clause: 'Upper-Right Diagonal Metric: Align head orientation into upper-right quadrant until ring turns green.'
  },
  {
    id: 'pose_7',
    number: 7,
    arrow: '↙️',
    stage: 'Pose_Variation_B',
    title: 'Pose 7 of 8: Compound Diagonal DOWN-LEFT',
    headline: 'Diagonal DOWN + LEFT',
    caption: 'Compound angle: Tilt chin downward (-15° to -25°) while rotating head left (+20° to +35°).',
    targetPitch: '-15° to -25° (Downward)',
    targetYaw: '+20° to +35° (Left)',
    rules: [
      { text: 'Simultaneously depress chin downward (-15° to -25°)', type: 'pitch' },
      { text: 'Rotate head toward your left (20° to 35°)', type: 'yaw' },
      { text: 'Maintain lower-left diagonal compound alignment', type: 'angle' },
      { text: 'Hold steady inside green target quadrant for automated capture', type: 'hold' }
    ],
    clause: 'Lower-Left Diagonal Metric: Align head orientation into lower-left quadrant until ring turns green.'
  },
  {
    id: 'pose_8',
    number: 8,
    arrow: '↘️',
    stage: 'Pose_Variation_B',
    title: 'Pose 8 of 8: Compound Diagonal DOWN-RIGHT',
    headline: 'Diagonal DOWN + RIGHT',
    caption: 'Compound angle: Tilt chin downward (-15° to -25°) while rotating head right (-20° to -35°).',
    targetPitch: '-15° to -25° (Downward)',
    targetYaw: '-20° to -35° (Right)',
    rules: [
      { text: 'Simultaneously depress chin downward (-15° to -25°)', type: 'pitch' },
      { text: 'Rotate head toward your right (-20° to -35°)', type: 'yaw' },
      { text: 'Maintain lower-right diagonal compound alignment', type: 'angle' },
      { text: 'Hold steady inside green target quadrant for automated capture', type: 'hold' }
    ],
    clause: 'Lower-Right Diagonal Metric: Align head orientation into lower-right quadrant until ring turns green.'
  }
];
